import {
  bucketForResultType,
  groupSearchResultsByBucket,
  labelForSearchResultBucket,
  normalizeSearchQuery,
  useSDK,
  useStores,
} from '@littlebird/sdk';
import { useKBar } from 'kbar';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { getSavedRoutineView } from '@/features/routines/routine-chat-view';
import { addSearchFocus } from '@/lib/search-focus';
import {
  COMMAND_BAR_SCOPES,
  type CommandBarScope,
  uiStore,
} from '@/stores/ui.store';
import { navigate } from '@/utils/navigation-service';

import { createAction } from './actions';
import { type Action } from './actions/types';
import {
  presentSearchCmdBarResults,
  type PresentedSearchCmdBarResult,
} from './search-result-presentation';

interface BuildSearchCmdBarActionsInput {
  query: string;
  searchResults: PresentedSearchCmdBarResult[];
  scope?: CommandBarScope;
}

export function isSearchQuery(query: string) {
  return query.length > 2;
}

const SEARCH_RESULT_ICON_SIZE = 14;
const SEARCH_SECTION_ROW_CAP = 3;
// Each row is a kbar action, so a scoped section also has a row limit
const SCOPED_SECTION_ROW_CAP = 50;
const SEARCH_SECTION_ORDER: string[] = COMMAND_BAR_SCOPES.slice(1);

interface SeeAllEntry {
  type: 'see_all';
  section: string;
  total: number;
}

function sectionRank(section: string) {
  const index = SEARCH_SECTION_ORDER.indexOf(section);
  return index === -1 ? SEARCH_SECTION_ORDER.length : index;
}

function getSearchResultSection(result: PresentedSearchCmdBarResult): string {
  if ('reportTemplateId' in result && result.reportTemplateId !== undefined) {
    return 'Routines';
  }
  return labelForSearchResultBucket(bucketForResultType(result.type));
}

function orderSearchEntries(
  searchResults: PresentedSearchCmdBarResult[],
  scope: CommandBarScope
): (PresentedSearchCmdBarResult | SeeAllEntry)[] {
  const bySection = new Map<string, PresentedSearchCmdBarResult[]>();
  for (const result of searchResults) {
    const section = getSearchResultSection(result);
    const rows = bySection.get(section);
    if (rows) rows.push(result);
    else bySection.set(section, [result]);
  }

  return [...bySection.entries()]
    .filter(([section]) => scope === 'All' || section === scope)
    .sort(([a], [b]) => sectionRank(a) - sectionRank(b))
    .flatMap(([section, rows]) => {
      if (scope !== 'All') return rows.slice(0, SCOPED_SECTION_ROW_CAP);
      if (sectionRank(section) >= SEARCH_SECTION_ORDER.length) return rows;
      if (rows.length <= SEARCH_SECTION_ROW_CAP) return rows;
      const seeAll: SeeAllEntry = {
        type: 'see_all',
        section,
        total: rows.length,
      };
      return [...rows.slice(0, SEARCH_SECTION_ROW_CAP), seeAll];
    });
}

export function buildSearchCmdBarActions({
  query,
  searchResults,
  scope = 'All',
}: BuildSearchCmdBarActionsInput): Action[] {
  if (!isSearchQuery(query)) {
    return [];
  }

  return orderSearchEntries(searchResults, scope)
    .map((item, index) => {
      const searchResultPriority = -9_999 - index;
      if (item.type === 'see_all') {
        return createAction({
          id: `search-see-all:${item.section}`,
          priority: searchResultPriority,
          name: `See all ${item.total} ${item.section.toLowerCase()}`,
          visible: () => true,
          section: item.section,
          icon: <Icons.arrowRight size={SEARCH_RESULT_ICON_SIZE} />,
          keywords: query,
          isSearchResult: true,
          keepOpen: true,
          preview: {
            kind: 'see_all',
            section: item.section,
            total: item.total,
            query,
          },
          perform: () => {
            uiStore.setCommandBarScope(item.section as CommandBarScope);
          },
        });
      }
      const section = getSearchResultSection(item);
      if (item.type === 'thread') {
        return createAction({
          id: item.id,
          priority: searchResultPriority,
          name: item.title,
          visible: () => true,
          section,
          icon:
            item.reportTemplateId === undefined ? (
              <Icons.chat size={SEARCH_RESULT_ICON_SIZE} />
            ) : (
              <Icons.routines size={SEARCH_RESULT_ICON_SIZE} />
            ),
          keywords: item.keywords,
          isSearchResult: true,
          date: item.updatedAt,
          preview:
            item.reportTemplateId === undefined
              ? {
                  kind: 'chat',
                  threadId: item.id,
                  title: item.title,
                  updatedAt: item.updatedAt,
                  mentionCount: item.mentionCount,
                  query,
                }
              : {
                  kind: 'routine',
                  reportTemplateId: item.reportTemplateId,
                  title: item.title,
                  updatedAt: item.updatedAt,
                  query,
                },
          perform: () => {
            if (item.reportTemplateId !== undefined) {
              navigate({
                to: '/routines/$routineId',
                params: { routineId: String(item.reportTemplateId) },
                search: { view: getSavedRoutineView(item.reportTemplateId) },
              });
              return;
            }
            navigate({
              to: '/chats/$chatId',
              params: {
                chatId: item.id,
              },
            });
          },
        });
      }

      if (item.type === 'message') {
        return createAction({
          id: item.id,
          priority: searchResultPriority,
          visible: () => true,
          name: item.matchedLine,
          section,
          icon:
            item.reportTemplateId === undefined ? (
              <Icons.messageSearch size={SEARCH_RESULT_ICON_SIZE} />
            ) : (
              <Icons.routines size={SEARCH_RESULT_ICON_SIZE} />
            ),
          keywords: item.keywords,
          isSearchResult: true,
          meta: item.title,
          date: item.updatedAt,
          preview:
            item.reportTemplateId === undefined
              ? {
                  kind: 'message',
                  threadId: item.threadId,
                  messageId: item.id,
                  chatTitle: item.title,
                  content: item.content,
                  updatedAt: item.updatedAt,
                  query,
                }
              : {
                  kind: 'routine',
                  reportTemplateId: item.reportTemplateId,
                  title: item.title,
                  content: item.content,
                  updatedAt: item.updatedAt,
                  query,
                },
          perform: () => {
            if (item.reportTemplateId !== undefined) {
              navigate({
                to: '/routines/$routineId',
                params: { routineId: String(item.reportTemplateId) },
                search: { view: getSavedRoutineView(item.reportTemplateId) },
              });
              return;
            }
            navigate({
              to: '/chats/$chatId',
              params: {
                chatId: item.threadId,
              },
              search: addSearchFocus(undefined, {
                query,
                bucketId: item.id,
                threadId: item.threadId,
                occurrence: 0,
              }),
            });
          },
        });
      }

      if (item.type === 'journal') {
        return createAction({
          id: item.id,
          priority: searchResultPriority,
          name: item.title,
          visible: () => true,
          section,
          icon: <Icons.journal size={SEARCH_RESULT_ICON_SIZE} />,
          keywords: item.keywords,
          isSearchResult: true,
          date: item.updatedAt,
          preview: {
            kind: 'journal',
            journalId: item.id,
            title: item.title,
            content: item.content,
            updatedAt: item.updatedAt,
            query,
          },
          perform: () => {
            navigate({
              to: '/journal/$journalId',
              params: {
                journalId: item.id,
              },
              search: addSearchFocus(undefined, {
                query,
                editorId: `journal-editor-${item.id}`,
                occurrence: 0,
              }),
            });
          },
        });
      }

      if (item.type === 'meeting_summary') {
        return createAction({
          id: item.id,
          priority: searchResultPriority,
          visible: () => true,
          name: item.title,
          section,
          icon: <Icons.meetings size={SEARCH_RESULT_ICON_SIZE} />,
          keywords: item.keywords,
          isSearchResult: true,
          date: item.updatedAt,
          preview: {
            kind: 'meeting',
            meetingId: item.meetingId,
            label: 'Meeting summary',
            title: item.title,
            content: item.content,
            updatedAt: item.updatedAt,
            query,
          },
          perform: () => {
            navigate({
              to: '/meetings/$meetingId',
              params: {
                meetingId: item.meetingId,
              },
              search: addSearchFocus(
                {
                  tab: 'summary',
                } as const,
                {
                  query,
                  editorId: `summary-editor-${item.meetingId}`,
                  occurrence: 0,
                }
              ),
            });
          },
        });
      }

      if (item.type === 'meeting_note') {
        return createAction({
          id: item.id,
          priority: searchResultPriority,
          visible: () => true,
          name: item.title,
          section,
          icon: <Icons.write size={SEARCH_RESULT_ICON_SIZE} />,
          keywords: item.keywords,
          isSearchResult: true,
          date: item.updatedAt,
          preview: {
            kind: 'meeting',
            meetingId: item.meetingId,
            label: 'Meeting note',
            title: item.title,
            content: item.content,
            updatedAt: item.updatedAt,
            query,
          },
          perform: () => {
            navigate({
              to: '/meetings/$meetingId',
              params: {
                meetingId: item.meetingId,
              },
              search: addSearchFocus(
                {
                  tab: 'notes',
                } as const,
                {
                  query,
                  editorId: `note-editor-${item.meetingId}`,
                  occurrence: 0,
                }
              ),
            });
          },
        });
      }

      return undefined;
    })
    .filter((action): action is Action => action !== undefined);
}

const NO_PRESENTED_RESULTS: PresentedSearchCmdBarResult[] = [];
const NO_SEARCH_ACTIONS = {
  actions: [] as Action[],
  totalCount: 0,
  sectionTotals: {} as Record<string, number>,
};

export function useSearchCmdBarActions(visible: boolean) {
  'use no memo';
  const { threads, routines } = useStores();
  const { searchStore } = useSDK();
  const query = uiStore.commandBarQuery;
  const { isInSubMenu } = useKBar((state) => ({
    isInSubMenu: Boolean(state.currentRootActionId),
  }));

  const results = visible ? searchStore.results : undefined;

  const debouncedSearchRef = useRef(
    debounce((searchInput: string) => {
      searchStore.search(searchInput);
    }, 300)
  );

  useEffect(() => {
    const debouncedSearch = debouncedSearchRef.current;
    if (!isInSubMenu && isSearchQuery(query)) {
      debouncedSearch(query);
    }
    return () => debouncedSearch.cancel();
  }, [query, isInSubMenu]);

  // Rank `results` with the query that made them. Then a keystroke does not
  // rank old results again before the debounced search starts
  const resultsQuery = searchStore.query;
  const showResults = isSearchQuery(query);

  let presentedResults = NO_PRESENTED_RESULTS;
  if (visible) {
    const normalized = normalizeSearchQuery(resultsQuery);
    const groups = normalized
      ? groupSearchResultsByBucket(results || [], normalized)
      : [];
    presentedResults = presentSearchCmdBarResults({
      searchResults: groups.flatMap((g) => g.items),
      query: resultsQuery,
      getThreadTitle: (threadId: string) => threads.get(threadId)?.title,
      getRoutineTitle: (reportTemplateId: number) =>
        routines.getByReferenceId(reportTemplateId)?.title,
    });
  }

  const scope = visible ? uiStore.commandBarScope : 'All';

  return useMemo(() => {
    if (!visible) return NO_SEARCH_ACTIONS;
    const sectionTotals: Record<string, number> = {};
    for (const result of presentedResults) {
      const section = getSearchResultSection(result);
      sectionTotals[section] = (sectionTotals[section] ?? 0) + 1;
    }
    return {
      actions: showResults
        ? buildSearchCmdBarActions({
            query: resultsQuery,
            searchResults: presentedResults,
            scope,
          })
        : [],
      totalCount: presentedResults.length,
      sectionTotals,
    };
  }, [visible, showResults, resultsQuery, presentedResults, scope]);
}
